import { Field, ID, ObjectType } from 'type-graphql'
import { Column, Entity, Index, ManyToOne, PrimaryGeneratedColumn, RelationId } from 'typeorm'

import {
  HistoryActionColumn,
  HistoryActionType,
  HistoryEntityInterface,
  HistoryOriginalIdColumn
} from '@operato/typeorm-history'
import { User } from '@things-factory/auth-base'
import { ApprovalLineItem } from '@things-factory/organization'
import { Domain, ScalarObject } from '@things-factory/shell'
import { AssigneeItem } from '@things-factory/worklist'
import { config } from '@things-factory/env'

import { DataItem } from '../data-set/data-item-type.js'
import {
  DataSet,
  DataSetEntryType,
  DataSetMonitorType,
  DataSetReportType,
  DataSetSummaryPeriodType
} from '../data-set/data-set.js'

const ORMCONFIG = config.get('ormconfig', {})
const DATABASE_TYPE = ORMCONFIG.type

@Entity()
@Index(
  'ix_data_set_history_0',
  (dataSetHistory: DataSetHistory) => [dataSetHistory.originalId, dataSetHistory.version],
  { unique: true }
)
@Index(
  'ix_data_set_history_1',
  (dataSetHistory: DataSetHistory) => [dataSetHistory.domain, dataSetHistory.originalId, dataSetHistory.version],
  { unique: true }
)
@ObjectType({
  description:
    'Represents a historical version of a dataset configuration. This entity tracks all changes made to datasets over time for audit and rollback purposes.'
})
export class DataSetHistory implements HistoryEntityInterface<DataSet> {
  @PrimaryGeneratedColumn('uuid')
  @Field(type => ID, { description: 'Unique identifier for the dataset history record.' })
  readonly id: string

  @Column({ default: 1 })
  @Field({ nullable: true, description: 'Version number of this historical dataset configuration.' })
  readonly version: number = 1

  @ManyToOne(type => Domain)
  @Field(type => Domain)
  domain?: Domain

  @RelationId((dataSetHistory: DataSetHistory) => dataSetHistory.domain)
  domainId?: string

  @Column()
  @Field({ description: 'Name of the dataset at the time of this historical version.' })
  name: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Description of the dataset at the time of this historical version.' })
  description?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Tag name of the dataset at the time of this historical version.' })
  tag?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Active status of the dataset at the time of this historical version.' })
  active?: boolean

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'ID of the data key set at the time of this historical version.' })
  dataKeySetId?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'ID of the entry role at the time of this historical version.' })
  entryRoleId?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'ID of the supervisory role at the time of this historical version.' })
  supervisoryRoleId?: string

  @Column('simple-json', { nullable: true })
  @Field(type => [AssigneeItem], {
    nullable: true,
    description: 'Assignee configuration at the time of this historical version.'
  })
  assignees?: AssigneeItem[]

  @Column('simple-json', { nullable: true })
  @Field(type => [ApprovalLineItem], {
    nullable: true,
    description: 'Review approval line configuration at the time of this historical version.'
  })
  reviewApprovalLine?: ApprovalLineItem[]

  @Column('simple-json', { nullable: true })
  @Field(type => [ApprovalLineItem], {
    nullable: true,
    description: 'Outlier approval line configuration at the time of this historical version.'
  })
  outlierApprovalLine?: ApprovalLineItem[]

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'ID of the normal scenario at the time of this historical version.' })
  normalScenarioId?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'ID of the outlier scenario at the time of this historical version.' })
  outlierScenarioId?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Type of the dataset at the time of this historical version.' })
  type?: 'manual' | 'automatic' | string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Entry type of the dataset at the time of this historical version.' })
  entryType?: DataSetEntryType

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Entry view of the dataset at the time of this historical version.' })
  entryView?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Monitor type of the dataset at the time of this historical version.' })
  monitorType?: DataSetMonitorType

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Monitor view of the dataset at the time of this historical version.' })
  monitorView?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Report type of the dataset at the time of this historical version.' })
  reportType?: DataSetReportType

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Report view of the dataset at the time of this historical version.' })
  reportView?: string

  @Field({ nullable: true, description: 'Report template of the dataset at the time of this historical version.' })
  reportTemplate?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Use case of the dataset at the time of this historical version.' })
  useCase?: string

  @Column('simple-json', { nullable: true })
  @Field(type => ScalarObject, {
    nullable: true,
    description: 'Partition keys configuration at the time of this historical version.'
  })
  partitionKeys?: { [key: string]: any }

  @Column('simple-json', { nullable: true })
  @Field(type => [DataItem], {
    nullable: true,
    description: 'Data items configuration at the time of this historical version.'
  })
  dataItems: DataItem[]

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Schedule configuration at the time of this historical version.' })
  schedule?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Schedule ID at the time of this historical version.' })
  scheduleId?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Timezone configuration at the time of this historical version.' })
  timezone?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Review requirement setting at the time of this historical version.' })
  requiresReview?: boolean

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Summary period configuration at the time of this historical version.' })
  summaryPeriod?: DataSetSummaryPeriodType

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Summary schedule configuration at the time of this historical version.' })
  summarySchedule?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Summary schedule ID at the time of this historical version.' })
  summaryScheduleId?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Creation timestamp of this historical version.' })
  createdAt?: Date

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Last update timestamp of this historical version.' })
  updatedAt?: Date

  @ManyToOne(type => User, { nullable: true })
  @Field(type => User, { nullable: true, description: 'User who created this historical version.' })
  creator?: User

  @RelationId((dataSetHistory: DataSetHistory) => dataSetHistory.creator)
  creatorId?: string

  @ManyToOne(type => User, { nullable: true })
  @Field(type => User, { nullable: true, description: 'User who last updated this historical version.' })
  updater?: User

  @RelationId((dataSetHistory: DataSetHistory) => dataSetHistory.updater)
  updaterId?: string

  @HistoryOriginalIdColumn({ nullable: true })
  @Field({ description: 'ID of the original dataset that this history record belongs to.' })
  public originalId!: string

  @HistoryActionColumn({
    nullable: false,
    type:
      DATABASE_TYPE == 'postgres' || DATABASE_TYPE == 'mysql' || DATABASE_TYPE == 'mariadb'
        ? 'enum'
        : DATABASE_TYPE == 'oracle'
          ? 'varchar2'
          : DATABASE_TYPE == 'mssql'
            ? 'nvarchar'
            : 'varchar',
    enum:
      DATABASE_TYPE == 'postgres' || DATABASE_TYPE == 'mysql' || DATABASE_TYPE == 'mariadb'
        ? HistoryActionType
        : undefined,
    length: DATABASE_TYPE == 'postgres' || DATABASE_TYPE == 'mysql' || DATABASE_TYPE == 'mariadb' ? undefined : 10
  })
  @Field({ description: 'Type of action performed on the dataset (INSERT, UPDATE, DELETE).' })
  public action!: HistoryActionType
}
